import { CommonModule, NgClass } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, combineLatest, takeUntil, startWith, debounceTime } from 'rxjs';
import { FaqService } from './faq.service';
import { SearchService } from '../services/search.service';

interface FaqItem {
  _id?: string;
  question: string;
  answer: string;
  open?: boolean;
}

@Component({
  selector: 'app-faq',
  standalone: true,
  imports: [CommonModule, NgClass],
  templateUrl: './faq.component.html',
  styleUrls: ['./faq.component.scss'],
})
export class FaqComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private faqService = inject(FaqService);
  private searchService = inject(SearchService);

  faqs: FaqItem[] = [];
  currentModule = '';
  currentProjectId = '';
  isLoading = false;
  error: string | null = null;

  private searchTerm: string = '';
  private currentModuleFilter: string = '';
  private currentSortOrder: 'asc' | 'desc' = 'desc';

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    // Get module from parent route
    this.route.parent?.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.currentModule = params.get('module') || '';
      this.loadProjectId(this.currentModule);
    });

    // Combine search, module filter, and sort order with initial values
    combineLatest([
      this.searchService.search$.pipe(startWith(''), debounceTime(300), takeUntil(this.destroy$)),
      this.searchService.moduleFilter$.pipe(startWith('all'), takeUntil(this.destroy$)),
      this.searchService.sortOrder$.pipe(startWith('desc'), takeUntil(this.destroy$)),
    ]).subscribe(([search, moduleFilter, sort]) => {
      this.searchTerm = search?.trim() || '';
      this.currentModuleFilter = moduleFilter === 'all' ? '' : moduleFilter || '';
      this.currentSortOrder = (sort === 'asc' || sort === 'desc') ? sort : 'desc';
      if (this.currentProjectId) this.loadFaqs();
    });
  }

  private loadProjectId(moduleSlug: string) {
    this.isLoading = true;
    this.error = null;
    this.faqService.getAllProjects().pipe(takeUntil(this.destroy$)).subscribe({
      next: (projects) => {
        const matched = projects.find(p => this.slugify(p.name) === moduleSlug);
        if (matched) {
          this.currentProjectId = matched._id;
          this.loadFaqs();
        } else {
          this.isLoading = false;
          this.error = 'No matching project found for this module.';
        }
      },
      error: (err) => {
        console.error('Failed to fetch projects', err);
        this.error = 'Failed to load project data';
        this.isLoading = false;
      },
    });
  }

  private loadFaqs() {
    if (!this.currentProjectId) return;
    this.isLoading = true;
    this.error = null;

    this.faqService.getFaqsByProject(
      this.currentProjectId,
      this.searchTerm,
      this.currentModuleFilter,
      this.currentSortOrder
    ).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any[]) => {
          this.faqs = res.map(f => ({
            _id: f._id,
            question: f.question,
            answer: f.answer,
            open: false,
          }));
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Failed to fetch FAQs', err);
          this.error = 'Failed to load FAQs';
          this.isLoading = false;
        }
      });
  }

  toggle(index: number) {
    this.faqs[index].open = !this.faqs[index].open;
  }

  slugify(name: string): string {
    return name.toLowerCase().replace(/\s+/g, '-');
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
